/* InsumoCardStack — card stack navegável por teclado (V2 LSC/EGN).
 * Recebe `insumos` (lista vinda da IA via /api/ai/extract-inputs).
 * Permite navegar com ← → ou A/D; aceitar com Enter ou ✓; descartar com Backspace ou ✗.
 * Estados por insumo: pending | accepted | discarded.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Edit3, RotateCcw, Sparkles, X } from "lucide-react";
import { CLASSIFICACOES, PESTEL, AREAS, TIPO_TRECHO } from "@/lib/constants";

function Pill({ children, color = "var(--text-muted)" }) {
  return (
    <span className="text-[10px] font-mono px-1.5 py-0.5 border rounded" style={{ borderColor: color, color }}>{children}</span>
  );
}

export default function InsumoCardStack({ insumos: initial = [], onConfirm, busy = false }) {
  const [items, setItems] = useState(() => initial.map((ins, i) => ({ ...ins, _idx: i, _status: "pending", _editing: false })));
  const [cursor, setCursor] = useState(0);
  // re-sync quando o pai trocar a lista de insumos
  useEffect(() => {
    const t = setTimeout(() => {
      setItems(initial.map((ins, i) => ({ ...ins, _idx: i, _status: "pending", _editing: false })));
      setCursor(0);
    }, 0);
    return () => clearTimeout(t);
  }, [initial]);

  const total = items.length;
  const acceptedCount = useMemo(() => items.filter((x) => x._status === "accepted").length, [items]);
  const current = items[cursor];

  const updateField = (k, v) => {
    setItems((arr) => arr.map((it, i) => (i === cursor ? { ...it, [k]: v } : it)));
  };
  const setStatus = (status) => {
    setItems((arr) => arr.map((it, i) => (i === cursor ? { ...it, _status: status, _editing: false } : it)));
  };
  const next = useCallback(() => setCursor((c) => Math.min(c + 1, total - 1)), [total]);
  const prev = useCallback(() => setCursor((c) => Math.max(c - 1, 0)), []);

  // Atalhos de teclado quando o card está em foco
  const onKey = useCallback((e) => {
    if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable)) return;
    if (e.key === "ArrowRight" || e.key === "d") { e.preventDefault(); next(); }
    else if (e.key === "ArrowLeft" || e.key === "a") { e.preventDefault(); prev(); }
    else if (e.key === "Enter") { e.preventDefault(); setStatus("accepted"); setTimeout(next, 100); }
    else if (e.key === "Backspace" || e.key === "x") { e.preventDefault(); setStatus("discarded"); setTimeout(next, 100); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, prev, cursor, total]);

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onKey]);

  if (total === 0) return null;

  const handleConfirm = () => {
    if (acceptedCount === 0) return;
    const accepted = items
      .filter((it) => it._status === "accepted")
      .map(({ _idx, _status, _editing, ...rest }) => rest);
    onConfirm && onConfirm(accepted);
  };

  const classifLabel = CLASSIFICACOES.find((c) => c.value === current.classificacao_potencial_semente_sugerida)?.label || current.classificacao_potencial_semente_sugerida;

  return (
    <div className="card-mil p-5 gold-accent-top" data-testid="insumo-cardstack">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[var(--gold)]" />
          <h3 className="font-display font-bold">Insumos Prospectivos Sugeridos</h3>
          <span className="badge badge-gold text-[10px]">Gemini 2.5</span>
        </div>
        <div className="text-[11px] text-[var(--text-muted)] font-mono">
          {cursor + 1} / {total} · <span className="text-[var(--success)]">{acceptedCount} aceitos</span>
        </div>
      </div>

      <div className="text-[10px] text-[var(--text-muted)] mb-3">
        Use <kbd className="px-1 bg-[var(--surface-2)] rounded border border-[var(--border)] font-mono">← →</kbd> para navegar, <kbd className="px-1 bg-[var(--surface-2)] rounded border border-[var(--border)] font-mono">Enter</kbd> para aceitar, <kbd className="px-1 bg-[var(--surface-2)] rounded border border-[var(--border)] font-mono">Backspace</kbd> para descartar.
      </div>

      <div className="relative border-2 rounded-lg p-4 transition-colors"
           style={{
             borderColor:
               current._status === "accepted" ? "var(--success)" :
               current._status === "discarded" ? "var(--danger)" : "var(--border)",
             background: current._status === "discarded" ? "var(--surface-2)" : "transparent",
             opacity: current._status === "discarded" ? 0.6 : 1,
           }}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            {current._editing ? (
              <input
                className="input-mil text-base font-bold"
                value={current.titulo_insumo || ""}
                onChange={(e) => updateField("titulo_insumo", e.target.value)}
                data-testid="insumo-edit-titulo"
              />
            ) : (
              <h4 className="font-display text-lg font-bold leading-snug" data-testid="insumo-titulo">{current.titulo_insumo || "(sem título)"}</h4>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Pill color="var(--gold)">{classifLabel}</Pill>
              <Pill color="var(--info)">{current.area_tematica_principal_sugerida || "Área?"}</Pill>
              <Pill color="var(--text-muted)">{current.tipo_trecho || "Parágrafo"}</Pill>
              {current.pagina_ou_secao && <Pill>p. {current.pagina_ou_secao}</Pill>}
              <Pill color="var(--gold)">Confiança {current.score_confianca_ia || 0}%</Pill>
            </div>
          </div>
          <div className="flex gap-1">
            <button type="button" onClick={() => updateField("_editing", !current._editing)} className="btn-ghost text-xs flex items-center gap-1" data-testid="insumo-edit-toggle">
              <Edit3 size={11} /> {current._editing ? "Fim" : "Editar"}
            </button>
          </div>
        </div>

        {/* Trecho literal */}
        <div className="mt-4">
          <div className="label-mil mb-1">Trecho literal</div>
          {current._editing ? (
            <textarea className="input-mil" rows={4}
                      value={current.trecho_literal || ""}
                      onChange={(e) => updateField("trecho_literal", e.target.value)}
                      data-testid="insumo-edit-trecho" />
          ) : (
            <div className="text-sm border-l-2 border-[var(--gold)] pl-3 italic text-[var(--text)] whitespace-pre-wrap" data-testid="insumo-trecho">
              {current.trecho_literal || "—"}
            </div>
          )}
        </div>

        {/* Tradução pt */}
        {(current.trecho_traduzido_pt || current._editing) && (
          <div className="mt-3">
            <div className="label-mil mb-1">Trecho em português</div>
            {current._editing ? (
              <textarea className="input-mil" rows={3}
                        value={current.trecho_traduzido_pt || ""}
                        onChange={(e) => updateField("trecho_traduzido_pt", e.target.value)} />
            ) : (
              <div className="text-sm text-[var(--text-muted)] whitespace-pre-wrap">{current.trecho_traduzido_pt || "—"}</div>
            )}
          </div>
        )}

        {/* Resumo + sinais */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="label-mil mb-1">Resumo do insumo</div>
            {current._editing ? (
              <textarea className="input-mil" rows={3}
                        value={current.resumo_do_insumo || ""}
                        onChange={(e) => updateField("resumo_do_insumo", e.target.value)} />
            ) : (
              <div className="text-[var(--text-muted)]">{current.resumo_do_insumo || "—"}</div>
            )}
          </div>
          <div>
            <div className="label-mil mb-1">Sinais identificados</div>
            <div className="flex flex-wrap gap-1">
              {(current.sinais_identificados || []).slice(0, 8).map((s) => (
                <span key={s} className="text-[10px] px-1.5 py-0.5 border border-[var(--info)] text-[var(--info)] rounded">{s}</span>
              ))}
              {!(current.sinais_identificados || []).length && <span className="text-[10px] text-[var(--text-muted)]">—</span>}
            </div>
            <div className="label-mil mt-2 mb-1">Palavras-chave</div>
            <div className="flex flex-wrap gap-1">
              {(current.palavras_chave || []).slice(0, 8).map((p) => (
                <span key={p} className="text-[10px] px-1.5 py-0.5 border border-[var(--border)] text-[var(--text-muted)] rounded">{p}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Edição extra de classificação */}
        {current._editing && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-[var(--border)]">
            <div>
              <label className="label-mil block mb-1">Classificação</label>
              <select className="input-mil" value={current.classificacao_potencial_semente_sugerida || "FPF"}
                      onChange={(e) => updateField("classificacao_potencial_semente_sugerida", e.target.value)}>
                {CLASSIFICACOES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mil block mb-1">Área temática</label>
              <select className="input-mil" value={current.area_tematica_principal_sugerida || "GLO"}
                      onChange={(e) => updateField("area_tematica_principal_sugerida", e.target.value)}>
                {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mil block mb-1">Tipo de trecho</label>
              <select className="input-mil" value={current.tipo_trecho || "Parágrafo"}
                      onChange={(e) => updateField("tipo_trecho", e.target.value)}>
                {TIPO_TRECHO.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="label-mil block mb-1">PESTEL</label>
              <div className="flex flex-wrap gap-1">
                {PESTEL.map((p) => {
                  const arr = current.perspectiva_pestel_sugerida || [];
                  const on = arr.includes(p.value);
                  return (
                    <button key={p.value} type="button"
                            onClick={() => updateField("perspectiva_pestel_sugerida", on ? arr.filter((x) => x !== p.value) : [...arr, p.value])}
                            className={`text-[10px] px-2 py-0.5 border rounded font-mono ${on ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--border)] text-[var(--text-muted)]"}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="label-mil block mb-1">Página / Seção</label>
              <input className="input-mil" value={current.pagina_ou_secao || ""}
                     onChange={(e) => updateField("pagina_ou_secao", e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          <button type="button" onClick={prev} disabled={cursor === 0} className="btn-ghost text-xs disabled:opacity-30" data-testid="insumo-prev">
            <ChevronLeft size={14} />
          </button>
          <button type="button" onClick={next} disabled={cursor === total - 1} className="btn-ghost text-xs disabled:opacity-30" data-testid="insumo-next">
            <ChevronRight size={14} />
          </button>
          {current._status !== "pending" && (
            <button type="button" onClick={() => setStatus("pending")} className="btn-ghost text-xs flex items-center gap-1" data-testid="insumo-reset">
              <RotateCcw size={12} /> Resetar
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => { setStatus("discarded"); setTimeout(next, 100); }}
                  className="px-3 py-1.5 text-xs border border-[var(--danger)] text-[var(--danger)] rounded hover:bg-[var(--danger)]/10 flex items-center gap-1"
                  data-testid="insumo-discard">
            <X size={12} /> Descartar
          </button>
          <button type="button" onClick={() => { setStatus("accepted"); setTimeout(next, 100); }}
                  className="px-3 py-1.5 text-xs border border-[var(--success)] text-[var(--success)] rounded hover:bg-[var(--success)]/10 flex items-center gap-1"
                  data-testid="insumo-accept">
            <Check size={12} /> Aceitar
          </button>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-[var(--border)] flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-[var(--text-muted)]">
          <strong>{acceptedCount}</strong> insumo{acceptedCount === 1 ? "" : "s"} pronto{acceptedCount === 1 ? "" : "s"} para virar Semente.
        </div>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy || acceptedCount === 0}
          className="btn-gold text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="insumo-confirm-create"
        >
          <Sparkles size={14} /> {busy ? "Criando…" : `Criar ${acceptedCount} Semente${acceptedCount === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}
